<template>
  <div class="table">
    <!-- 表头 -->
    <div v-if="title" class="table-title">{{ title }}</div>

    <!-- 表格主体 -->
    <el-table
      :ref="ref"
      v-loading="listLoading"
      :data="tableData"
      :row-key="rowKey || undefined"
      :element-loading-text="loadingText"
      :header-cell-style="{ backgroundColor: '#FAFAFA', color: '#333' }"
      :row-class-name="rowClassName"
      empty-text="暂无数据"
      @selection-change="handleSelectionChange"
    >
      <!-- 多选配置 -->
      <el-table-column
        v-if="isSelection"
        type="selection"
        width="55"
        :reserve-selection="reserveSelection"
      ></el-table-column>

      <!-- 表格索引 -->
      <el-table-column
        v-if="isShowNumber"
        fixed="left"
        type="index"
        label="ID"
        :index="tableIndex"
      ></el-table-column>

      <!-- 表格行  item.showOverflowTooltip 设置表格文字过长显示省略号 -->
      <el-table-column
        v-for="(item, index) in config"
        :key="index"
        :value="item.value"
        :label="item.label"
        :width="getColumnWidth(item)"
        :min-width="getColumnMinWidth(item)"
        :fixed="item.fixed"
        :show-overflow-tooltip="!['wrap', 'stack', 'highlight'].includes(item.format) && item.showOverflowTooltip !== false"
      >
        <template v-if="item.headerTooltip" slot="header">
          <span>{{ item.label }}</span>
          <el-tooltip :content="item.headerTooltip" placement="top">
            <i class="el-icon-question table-header-tip"></i>
          </el-tooltip>
        </template>
        <template slot-scope="scope">
          <!-- format = img, 显示图片 -->
          <span v-if="item.format === 'img'">
            <img
              title="点击查看大图"
              alt="图片"
              class="image-size"
              :src="scope.row[item.value]"
            />
          </span>

          <!-- format = timestamp, 把时间戳转换成YYYY-MM-dd HH:mm:ss ，parseTime过滤器后文会提到   -->
          <span v-if="item.format === 'timestamp'">{{
            scope.row[item.value] | timeFormat
          }}</span>

          <div class="wrap-text" v-else-if="item.format === 'wrap'">
            {{ scope.row[item.value] }}
          </div>

          <div
            class="stack-cell"
            :class="getStackCellClass(scope.row, item)"
            v-else-if="item.format === 'stack'"
          >
            <div v-if="item.imgValue" class="stack-cell__logo">
              <img
                v-if="scope.row[item.imgValue]"
                class="stack-cell__logo-img"
                :src="scope.row[item.imgValue]"
                alt=""
              />
              <span v-else class="stack-cell__logo-fallback">
                {{ (scope.row[item.value] || '?').charAt(0) }}
              </span>
            </div>
            <div class="stack-cell__content">
              <div
                class="stack-cell__title"
                :class="{ 'stack-cell__line-clamp': isLongStackTitle(scope.row, item) }"
                :title="isLongStackTitle(scope.row, item) ? scope.row[item.value] : undefined"
              >
                <template
                  v-if="item.highlightKeyword"
                >
                  <template
                    v-for="(part, partIndex) in getHighlightParts(
                      scope.row[item.value],
                      item.highlightKeyword
                    )"
                  >
                    <mark
                      v-if="part.match"
                      :key="'t-' + partIndex"
                      class="keyword-mark"
                    >{{ part.text }}</mark>
                    <span v-else :key="'t-' + partIndex">{{ part.text || '-' }}</span>
                  </template>
                </template>
                <template v-else>{{ scope.row[item.value] || '-' }}</template>
              </div>
              <div
                v-if="scope.row[item.subValue]"
                class="stack-cell__desc"
                :class="{ 'stack-cell__line-clamp': isLongStackDesc(scope.row, item) }"
                :title="isLongStackDesc(scope.row, item) ? scope.row[item.subValue] : undefined"
              >{{ scope.row[item.subValue] }}</div>
            </div>
          </div>

          <!-- format = money, 显示金额 -->
          <span v-else-if="item.format === 'money'">{{
            "￥" + scope.row[item.value]
          }}</span>

          <!-- format = dangerMoney, 负金额醒目显示 -->
          <span v-else-if="item.format === 'dangerMoney'" class="table-danger-money">
            <template v-if="isNegativeAmount(scope.row[item.value])">
              <el-tag type="danger" size="mini" effect="dark">欠费</el-tag>
              <strong class="table-danger-money__num">{{ scope.row[item.value] }}</strong>
            </template>
            <template v-else>{{ scope.row[item.value] }}</template>
          </span>

          <!-- format = rate, 显示比例 -->
          <span v-else-if="item.format === 'rate'">{{
            scope.row[item.value] + "%"
          }}</span>

          <span v-else-if="item.format === 'input'">
            <el-input
              v-model="scope.row[item.value]"
              @change="handleInputChange(scope.row[item.value])"
            >
              <template slot="append">%</template>
            </el-input>
          </span>

          <!-- format = number, 显示数字-->
          <span v-else-if="item.format === 'number'">{{
            Number(scope.row[item.value])
          }}</span>

          <span v-else-if="item.format === 'status'">{{
            +scope.row[item.value] === 0 ? "启用" : "暂停"
          }}</span>

          <!-- format = a, 网页跳转 -->
          <span v-else-if="item.format === 'a'">
            <u class="link">
              <a :href="scope.row[item.value]" target="_blank">{{
                scope.row[item.value]
              }}</a>
            </u>
          </span>

          <span v-else-if="item.format === 'highlight'" class="highlight-text">
            <template
              v-for="(part, partIndex) in getHighlightParts(
                scope.row[item.value],
                item.highlightKeyword
              )"
            >
              <mark
                v-if="part.match"
                :key="partIndex"
                class="keyword-mark"
              >{{ part.text }}</mark>
              <span v-else :key="partIndex">{{ part.text }}</span>
            </template>
          </span>

          <!-- 需要添加其他数据处理，继续添加 v-else-if="item.format === ''" 就好 -->

          <!-- 没有format -->
          <span v-else>{{ scope.row[item.value] }}</span>
        </template>
      </el-table-column>

      <el-table-column width="55" v-if="isMulSelect">
        <template slot="header">
          <el-checkbox
            :indeterminate="isIndeterminate"
            v-model="checkAll"
            @change="handleCheckAllChange"
          ></el-checkbox>
        </template>
        <template slot-scope="scope">
          <el-checkbox-group v-model="checkData" @change="handleCheckedChange">
            <el-checkbox :label="scope.row.storeId">{{ "" }}</el-checkbox>
          </el-checkbox-group>
        </template>
      </el-table-column>

      <el-table-column width="55" label="选择" v-if="isRadio">
        <template slot-scope="scope">
          <el-radio
            class="radio"
            :label="scope.row.channelId"
            v-model="radio"
            @change.native="getCurrentRow(scope.row)"
            >{{ "" }}</el-radio
          >
        </template>
      </el-table-column>

      <!-- 表格操作按钮栏 -->
      <el-table-column
        v-if="isHasButtons"
        class="clearfix"
        :width="optionColumnWidth + 'px'"
        fixed="right"
        label="操作"
      >
        <template slot-scope="scope">
          <!-- 基本操作 -->
          <span
            v-for="(option, index) in getOptionsName(scope.row)"
            :key="index"
            class="button-margin-left"
          >
            <template v-if="option.label === '查看'">
              <router-link :to="`${$route.path}/${scope.row[option.route]}`">
                <span>{{ option.label }}</span>
              </router-link>
            </template>
            <span
              v-else
              @click="
                handleClickOption(scope.$index, scope.row, option, $event)
              "
              >{{ option.label }}</span
            >
            <!--  <el-button
              size="small"
              :type="index == 0 ? 'primary' : ''"
              @click="handleClickOption(scope.$index, scope.row, option,$event)"
            >
              <span v-html="getButtonHtml(option)"></span>
            </el-button> -->
          </span>
        </template>
      </el-table-column>

      <!-- 订单列表页表格自定义操作按钮栏 -->
      <el-table-column
        v-if="isStatusButtons"
        class="clearfix"
        :width="optionColumnWidth + 'px'"
        fixed="right"
        label="操作"
      >
        <template slot-scope="scope">
          <!-- 基本操作 -->
          <span class="button-margin-left">
            <router-link :to="`${$route.path}/${scope.row.voucherId}`">
              <span>查看</span>
            </router-link>
          </span>
          <span
            :class="[
              'button-margin-left',
              filterColor(scope.row) ? '' : 'gray',
            ]"
          >
            <span @click="handleCustomClickOption(scope.row)">{{
              scope.row.operationSpecialStatus
            }}</span>
          </span>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页组件 -->
    <div
      v-if="isShowPagination && listQueryParams.total > 0"
      class="pagination-container"
    >
      <el-pagination
        background
        layout="sizes, prev, pager, next, jumper"
        :page-sizes="pageSizeList"
        :current-page="listQueryParams.pageNum"
        :page-size="listQueryParams.pageSize"
        :total="listQueryParams.total"
        @current-change="handleCurrentChange"
        @size-change="handleSizeChange"
      ></el-pagination>
    </div>
  </div>
</template>

<script>
import Cookies from "js-cookie";
export default {
  name: "AppTable",
  props: {
    // 是否需要序号
    isShowNumber: {
      type: Boolean,
      default: false,
    },
    // 是否需要翻页组件
    isShowPagination: {
      type: Boolean,
      default: true,
    },
    // 是否需要栅格
    isBorder: {
      type: Boolean,
      default: true,
    },
    // 是否是多选表格，默认非多选 :ref="'multipleTable'"
    isSelection: {
      type: Boolean,
      default: false,
    },
    isMulSelect: {
      type: Boolean,
      default: false,
    },
    isRadio: {
      type: Boolean,
      default: false,
    },
    // 是否有操作按钮
    isHasButtons: {
      type: Boolean,
      default: true,
    },
    // 是否有根据数据自定义多种操作按钮
    isStatusButtons: {
      type: Boolean,
      default: false,
    },
    // 表头名称
    title: {
      type: String,
    },
    rowClassName: {
      type: Function,
      default: null,
    },
    // 表格数据
    tableData: {
      type: Array,
      required: true,
    },
    // 表格配置信息
    config: {
      type: Array,
      required: true,
    },
    // loading提示框
    loadingText: {
      type: String,
      default: "加载中...",
    },
    // loading状态
    loadingStatus: {
      type: Boolean,
      default: false,
    },
    // 表格查询列表参数
    listQueryParams: {
      type: Object,
    },
    // 根据当前行的状态显示按钮的名称
    buttonsName: {
      type: Array,
    },
    // 操作列表宽度
    optionColumnWidth: {
      type: Number,
      default: 100,
    },
    // 分页数据
    pageSizeList: {
      type: Array,
      default: function () {
        return [10, 20, 30, 50, 100];
      },
    },
    initCheckData: {
      type: Array,
      default: function () {
        return [];
      },
    },
    initRadio: {
      type: String,
      default: "",
    },
    rowKey: {
      type: String,
      default: "",
    },
    reserveSelection: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isIndeterminate: false,
      checkAll: false,
      allData: [],
      checkData: [],
      radio: "",
    };
  },
  // filters: {
  //   timeFormat() {
  //     return this.timeFormat;
  //   }
  // },
  created() {
    this.checkData = [...(this.initCheckData || [])];
    setTimeout(() => {
      this.radio = this.initRadio;
    }, 500);
  },
  watch: {
    initCheckData(val) {
      this.checkData = [...(val || [])];
      this.syncPageCheckState();
    },
    tableData() {
      this.syncPageCheckState();
    },
  },

  computed: {
    // 看是否是多选表格
    ref: function () {
      return this.isSelection ? "multipleTable" : undefined;
    },
    // 第几页
    page: function () {
      return this.listQueryParams.pageNum || 1;
    },
    // 每页数据数
    pageSize: function () {
      return this.listQueryParams.pageSize || 10;
    },
    // 数据总数
    total: function () {
      return this.listQueryParams.total || 0;
    },
    // 获取当前loading的状态
    listLoading: function () {
      return this.loadingStatus;
    },
    filterColor: function (row) {
      return function (row) {
        const type = +Cookies.get("type");
        if (+row.status === 0 && [1, 2].includes(type)) return true;
        if ([1].includes(+row.status) && [1,3, 4].includes(type)) return true;
        return false;
      };
    },
  },
  methods: {
    isNegativeAmount(val) {
      const n = parseFloat(String(val == null ? "" : val).replace(/,/g, ""));
      return !Number.isNaN(n) && n < 0;
    },
    handleInputChange(val) {
      console.log(
        "🔍 ~ handleInputChange ~ src/components/Table/index.vue:319 ~ val:",
        val
      );
      this.$emit("getCurrentRow", this.radio);
    },
    getCurrentRow(val) {
      console.log("🚀 ~ getCurrentRow ~ val:", val);
      this.$emit("getCurrentRow", this.radio);
    },
    handleCheckAllChange(val) {
      this.allData = this.tableData.map((item) => item.storeId);
      this.checkData = val ? [...this.allData] : [];
      this.isIndeterminate = false;
      this.checkAll = val;
      this.$emit("subCheckAll", this.checkData);
    },
    handleCheckedChange(value) {
      this.syncPageCheckState(value);
      this.$emit("subCheckedData", value);
    },
    syncPageCheckState(checked = this.checkData) {
      const pageIds = this.tableData.map((item) => item.storeId);
      const checkedCount = pageIds.filter((id) => checked.includes(id)).length;
      this.checkAll = pageIds.length > 0 && checkedCount === pageIds.length;
      this.isIndeterminate =
        checkedCount > 0 && checkedCount < pageIds.length;
    },
    // 获取当前操作的按钮组
    getOptionsName(row) {
      if (row && Array.isArray(row.actionButtons)) {
        return row.actionButtons;
      }
      return this.buttonsName || [];
    },

    // 点击按钮传递给父组件
    handleClickOption(index, row, option) {
      this.$emit("subOpitonButton", index, row, option);
    },
    // 点击自定义按钮传递给父组件
    handleCustomClickOption(row) {
      this.$emit("subOpitonButton", row);
    },

    // 表格选择分发事件
    handleSelectionChange(val) {
      this.$emit("subSelected", val);
    },

    // 改变翻页组件中每页数据总数
    handleSizeChange(val) {
      this.listQueryParams.pageSize = val;
      // 改变翻页数目，将页面=1
      this.listQueryParams.pageNum = 1;
      this.$emit("update:listQueryParams", this.listQueryParams);
      this.$emit("subClickPagination", this.listQueryParams);
    },

    // 跳到当前是第几页
    handleCurrentChange(val) {
      this.listQueryParams.pageNum = val;
      this.$emit("update:listQueryParams", this.listQueryParams);
      this.$emit("subClickPagination", this.listQueryParams);
    },

    // 重写索引生成方法
    tableIndex(index) {
      const i = (this.page - 1) * this.pageSize + index + 1;
      return i;
    },

    getColumnWidth(item) {
      if (item.autoWidth) {
        return undefined;
      }
      if (item.grow) return undefined;
      return item.fixedWidth ? item.width : undefined;
    },

    getColumnMinWidth(item) {
      if (item.autoWidth) {
        return this.calcAutoMinWidth(item);
      }
      return item.minWidth || item.width;
    },

    getAutoWidthText(row, item) {
      const raw = row[item.value];
      if (raw == null || raw === "") return "";
      if (item.format === "status") {
        return +raw === 0 ? "启用" : "暂停";
      }
      if (item.format === "rate") {
        return String(raw) + "%";
      }
      if (item.format === "money" || item.format === "dangerMoney") {
        return "￥" + String(raw);
      }
      return String(raw);
    },

    calcAutoMinWidth(item) {
      const fallback = parseInt(item.minWidth || 200, 10);
      const max = parseInt(item.maxWidth || 560, 10);
      const headerWidth = this.measureTextWidth(item.label || "");
      const floor = parseInt(
        item.floorWidth || Math.max(60, headerWidth + 24),
        10
      );
      if (!this.tableData || !this.tableData.length) {
        return fallback;
      }

      const logoPad = item.imgValue ? 46 : 0;
      const cellPad = 32;
      let contentMax = headerWidth;

      this.tableData.forEach((row) => {
        const title = this.getAutoWidthText(row, item);
        const desc =
          item.subValue && row[item.subValue] ? String(row[item.subValue]) : "";
        if (title) {
          contentMax = Math.max(contentMax, this.measureTextWidth(title));
        }
        if (desc) {
          contentMax = Math.max(contentMax, this.measureTextWidth(desc));
        }
      });

      const needed = contentMax + logoPad + cellPad;
      return Math.min(max, Math.max(floor, needed));
    },

    getStackCellClass(row, item) {
      const title = String(row[item.value] || '');
      const desc = String(row[item.subValue] || '');
      return {
        'stack-cell--with-logo': !!item.imgValue,
        'stack-cell--compact': title.length <= 10 && (!desc || desc.length <= 16),
        'stack-cell--single': !desc,
      };
    },

    isLongStackTitle(row, item) {
      return String(row[item.value] || '').length > 20;
    },

    isLongStackDesc(row, item) {
      return String(row[item.subValue] || '').length > 28;
    },

    measureTextWidth(text) {
      let width = 0;
      for (const ch of text) {
        width += ch.charCodeAt(0) > 255 ? 14 : 8;
      }
      return width;
    },

    getHighlightParts(text, keyword) {
      const source = text == null ? "" : String(text);
      const key = (keyword || "").trim();
      if (!source || !key) {
        return [{ text: source, match: false }];
      }
      const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const re = new RegExp(escaped, "gi");
      const parts = [];
      let lastIndex = 0;
      let match = re.exec(source);
      while (match) {
        if (match.index > lastIndex) {
          parts.push({
            text: source.slice(lastIndex, match.index),
            match: false,
          });
        }
        parts.push({ text: match[0], match: true });
        lastIndex = match.index + match[0].length;
        if (match[0].length === 0) {
          re.lastIndex += 1;
        }
        match = re.exec(source);
      }
      if (lastIndex < source.length) {
        parts.push({ text: source.slice(lastIndex), match: false });
      }
      return parts.length ? parts : [{ text: source, match: false }];
    },
  },
};
</script>

<style lang="scss" scoped>
.table {
  background: #fff;
  padding: 20px;
}
/* 设置需要换行的列的样式 */
.wrap-text {
  white-space: normal; /* 允许文本换行 */
  word-wrap: break-word; /* 当单词太长时进行换行 */
}
.stack-cell {
  white-space: normal;
  word-break: break-word;
  line-height: 1.45;
  padding: 4px 0;
}
.stack-cell--with-logo {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.stack-cell--with-logo.stack-cell--single {
  align-items: center;
}
.stack-cell--compact {
  padding: 2px 0;
}
.stack-cell--compact .stack-cell__desc {
  margin-top: 2px;
}
.stack-cell__logo {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  overflow: hidden;
  background: #f5f7fa;
}
.stack-cell__logo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.stack-cell__logo-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 14px;
  font-weight: 600;
  color: #909399;
}
.stack-cell__content {
  flex: 1;
  min-width: 0;
}
.stack-cell__title {
  font-weight: 500;
  color: #303133;
}
.stack-cell__desc {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}
.stack-cell__title.stack-cell__line-clamp {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.stack-cell__desc.stack-cell__line-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.image-size {
  width: 30px;
  height: 30px;
  cursor: pointer;
}
.table-title {
  margin-top: 10px;
  font-size: 18px;
}
.button-margin-left {
  cursor: pointer;
  color: #1890ff;
  font-size: 14px;
}
.gray {
  color: #606266;
}
.button-margin-left:not(:first-child) {
  margin-left: 16px;
}

.link {
  cursor: pointer;
  color: #4876ff;
}
.table-header-tip {
  margin-left: 4px;
  color: #909399;
  cursor: help;
  font-size: 14px;
}
.table-danger-money {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.table-danger-money__num {
  color: #cf1322;
  font-size: 15px;
}
.highlight-text {
  white-space: normal;
  word-break: break-word;
  line-height: 1.5;
}
.keyword-mark {
  background: #1890ff;
  color: #fff;
  padding: 0 2px;
  border-radius: 2px;
  font-style: inherit;
}
</style>

<style lang="scss">
.table {
  .el-table {
    .el-table__empty-block {
      height: unset;
    }
  }
  .custom-dropdown-menu {
    cursor: pointer;
    .custom-dropdown-menu-item {
      margin-top: 10px;
      text-align: center;
    }
    .el-dropdown-menu__item:focus,
    .el-dropdown-menu__item:not(.is-disabled):hover {
      background-color: #fff;
      color: #fff;
    }
  }
  .el-table th:first-child > .cell {
    padding-left: 14px;
  }
  .el-table .cell {
    word-break: keep-all;
  }
  .el-table .cell .stack-cell,
  .el-table .cell .highlight-text {
    word-break: break-word;
  }
  .pagination-container {
    .el-pagination {
      text-align: right;
      margin-top: 16px;
    }
    .el-pagination.is-background .btn-next,
    .el-pagination.is-background .btn-prev,
    .el-pagination.is-background .el-pager li {
      background: #fff;
      border: 1px solid #d9d9d9;
    }
    .el-pagination.is-background .el-pager li:not(.disabled).active {
      background-color: #fff;
      color: #1890ff;
      border: 1px solid #1890ff;
    }
  }
}
</style>
